/**
 * User-portal sidebar (PRD §9.2).
 *
 * Two modes derived from the current path:
 *   - 'chats' (on /chat/*)  — only the Chats section (one row per accessible agent).
 *   - 'menu'  (everywhere else) — hides Chats; shows Sheets + Files + all
 *                                  granted feature tabs.
 *
 * Every row renders through the shared `SidebarNavItem` primitive so
 * shape / active-state / spacing can't drift between rows.
 */
import type {
  AgentSummary,
  FolderSummary,
  SheetSummary,
  SupportOdooHelpdeskStatus,
} from '@peerless/shared';
import { useQuery } from '@tanstack/react-query';
import {
  Archive,
  BookUser,
  Bot,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Folder as FolderIconLucide,
  FolderOpen,
  Gauge,
  Headset,
  Inbox,
  LayoutTemplate,
  LifeBuoy,
  MessageSquare,
  Scissors,
  Search,
  Sparkles,
  Table2,
  PhoneIncoming,
  ScrollText,
  Ticket,
  TrendingUp,
  Users,
  Wand2,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { useSupportMonitorAccess } from '../../hooks/useSupportMonitorAccess';
import { apiRequest } from '../../lib/api-client';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query-keys';
import { SheetIcon } from '../admin/SheetIcon';
import { SidebarNavItem } from '../ds';


const COLLAPSE_KEY = 'pp.user-sidebar.collapsed';
const CHATS_OPEN_KEY = 'pp.user-sidebar.chats-open';
const SHEETS_OPEN_KEY = 'pp.user-sidebar.sheets-open';
const FILES_OPEN_KEY = 'pp.user-sidebar.files-open';

type IconComponent = ComponentType<{ className?: string | undefined }>;

// Data-driven feature list — one row per granted module. The order here is
// the order the rows appear in the sidebar. To add a module, add one entry
// and gate it on the appropriate `has*` flag below.
interface FeatureRow {
  to: string;
  label: string;
  icon: IconComponent;
  visible: boolean;
}

export function UserSidebar(): JSX.Element {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const mode: 'chats' | 'menu' = pathname.startsWith('/chat') ? 'chats' : 'menu';

  const [collapsed, setCollapsed] = useState<boolean>(() => readBool(COLLAPSE_KEY, false));
  const [chatsOpen, setChatsOpen] = useState<boolean>(() => readBool(CHATS_OPEN_KEY, true));
  const [sheetsOpen, setSheetsOpen] = useState<boolean>(() => readBool(SHEETS_OPEN_KEY, true));
  const [filesOpen, setFilesOpen] = useState<boolean>(() => readBool(FILES_OPEN_KEY, true));

  useEffect(() => writeBool(COLLAPSE_KEY, collapsed), [collapsed]);
  useEffect(() => writeBool(CHATS_OPEN_KEY, chatsOpen), [chatsOpen]);
  useEffect(() => writeBool(SHEETS_OPEN_KEY, sheetsOpen), [sheetsOpen]);
  useEffect(() => writeBool(FILES_OPEN_KEY, filesOpen), [filesOpen]);

  const uid = user?.id ?? '';
  const enabled = !!user;

  const agentsQuery = useQuery({
    queryKey: queryKeys.userAgents.list(uid),
    queryFn: () => apiRequest<AgentSummary[]>(`/users/${uid}/agents`),
    enabled,
  });
  const sheetsQuery = useQuery({
    queryKey: queryKeys.userSheets.list(uid),
    queryFn: () => apiRequest<SheetSummary[]>(`/users/${uid}/sheets`),
    enabled,
  });
  const foldersQuery = useQuery({
    queryKey: queryKeys.userFolders.list(uid),
    queryFn: () => apiRequest<FolderSummary[]>(`/users/${uid}/folders`),
    enabled,
  });

  // Access-grant queries — one per module. Kept inline (rather than under a
  // shared `useAccess` helper) because react-query's typed overloads reject
  // a wrapped call with a widened `queryKey` type.
  const mediaCalendarAccess = useAccessQuery('/media-calendar/access', queryKeys.mediaCalendar.access(uid), enabled);
  const supportAccess = useAccessQuery('/support/access', queryKeys.support.access(uid), enabled);
  const helpdeskQuery = useQuery({
    queryKey: queryKeys.support.odooHelpdesk(),
    queryFn: () => apiRequest<SupportOdooHelpdeskStatus>('/support/odoo-helpdesk'),
    enabled: enabled && supportAccess,
    staleTime: 60_000,
  });
  // UST-3159 — Support Contracts has its own grant, separate from Support.
  const monitorAccess = useSupportMonitorAccess();
  const supportContractsAccess = useAccessQuery(
    '/support-contracts/access',
    queryKeys.supportContracts.myAccess(uid),
    enabled,
  );
  const crmAccess = useAccessQuery('/crm/access', queryKeys.crm.access(uid), enabled);
  const contentCalendarAccess = useAccessQuery('/content-calendar/access', queryKeys.contentCalendar.access(uid), enabled);
  const visualsGeneratorAccess = useAccessQuery('/visuals-generator/access', queryKeys.visualsGenerator.access(uid), enabled);
  const templateFillAccess = useAccessQuery('/template-fill/access', queryKeys.templateFill.access(uid), enabled);
  const videoEditorAccess = useAccessQuery('/video-editor/access', queryKeys.videoEditor.access(uid), enabled);
  const leadsAccess = useAccessQuery('/leads/access', queryKeys.leads.access(uid), enabled);
  const hrAccess = useAccessQuery('/hr/access', queryKeys.hr.access(uid), enabled);
  const seoAccess = useAccessQuery('/seo/access', queryKeys.seo.access(uid), enabled);
  // Scraper — sidebar entry shows only when the user has access to at least
  // one active scraping agent. Reuse the existing /scraper/agents endpoint
  // (returns the accessible list) so we don't add a new access route.
  const scraperAgentsQuery = useQuery({
    queryKey: queryKeys.scraper.agentsUser(),
    queryFn: () => apiRequest<unknown[]>('/scraper/agents'),
    enabled,
    staleTime: 60_000,
  });

  const agents = (agentsQuery.data ?? []).filter((a) => a.isActive);
  const sheets = (sheetsQuery.data ?? []).filter((s) => s.isActive);
  const folders = (foldersQuery.data ?? []).filter((f) => f.isActive);
  const hasScraper = (scraperAgentsQuery.data?.length ?? 0) > 0;

  // Feature rows are data-driven. Adding a new module = one new entry here.
  const features: FeatureRow[] = [
    { to: '/agents', label: 'AI Agents', icon: Bot, visible: mode === 'menu' && agents.length > 0 },
    { to: '/media-calendar', label: 'Media Calendar', icon: CalendarDays, visible: mode === 'menu' && mediaCalendarAccess },
    { to: '/support', label: 'Support Inbox', icon: LifeBuoy, visible: mode === 'menu' && supportAccess },
    { to: '/support-tickets', label: 'Support Tickets', icon: Ticket, visible: mode === 'menu' && supportAccess },
    { to: '/support-monitor', label: 'Support Monitor', icon: Gauge, visible: mode === 'menu' && monitorAccess.allowed },
    {
      to: '/telephony-support',
      label: 'Telephony Support',
      icon: PhoneIncoming,
      visible: mode === 'menu' && supportAccess,
    },
    {
      to: '/support-contacts',
      label: 'Support Contacts',
      icon: BookUser,
      visible: mode === 'menu' && supportAccess,
    },
    {
      to: '/support-contracts',
      label: 'Support Contracts',
      icon: ScrollText,
      visible: mode === 'menu' && supportContractsAccess,
    },
    { to: '/support-archive', label: 'Support Archive', icon: Archive, visible: mode === 'menu' && supportAccess },
    // UST-3143 — only while Odoo Helpdesk sync is on.
    {
      to: '/odoo-helpdesk',
      label: 'Odoo Helpdesk',
      icon: Headset,
      visible: mode === 'menu' && supportAccess && !!helpdeskQuery.data?.enabled,
    },
    { to: '/content-calendar', label: 'Content Calendar', icon: Sparkles, visible: mode === 'menu' && contentCalendarAccess },
    { to: '/visuals-generator', label: 'Visuals Generator', icon: Wand2, visible: mode === 'menu' && visualsGeneratorAccess },
    { to: '/hr', label: 'Human Resources', icon: Building2, visible: mode === 'menu' && hrAccess },
    { to: '/seo', label: 'SEO', icon: TrendingUp, visible: mode === 'menu' && seoAccess },
    { to: '/scraper', label: 'Scraper', icon: Search, visible: mode === 'menu' && hasScraper },
    { to: '/video-editor', label: 'Video Editor', icon: Scissors, visible: mode === 'menu' && videoEditorAccess },
    // Mail lives in the top-bar workspace switcher (it's universal, not a
    // gated module) — see WorkspaceSwitcher.
    { to: '/template-fill', label: 'Template Fill', icon: LayoutTemplate, visible: mode === 'menu' && templateFillAccess },
    { to: '/leads', label: 'Contact us', icon: Inbox, visible: mode === 'menu' && leadsAccess },
    { to: '/crm', label: 'CRM', icon: Users, visible: mode === 'menu' && crmAccess },
  ];
  const visibleFeatures = features.filter((f) => f.visible);

  const showChats = mode === 'chats' && agents.length > 0;
  const showSheets = mode === 'menu' && sheets.length > 0;
  const showFiles = mode === 'menu' && folders.length > 0;

  const nothingAssigned =
    !agentsQuery.isLoading &&
    !sheetsQuery.isLoading &&
    !foldersQuery.isLoading &&
    agents.length === 0 &&
    sheets.length === 0 &&
    folders.length === 0 &&
    visibleFeatures.length === 0;

  return (
    <aside
      className={cn(
        'flex shrink-0 flex-col border-r border-rule bg-paper-soft transition-[width] duration-200 ease-standard',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
      aria-label="Primary navigation"
    >
      <nav className="flex-1 overflow-y-auto py-2">
        {showChats && (
          <>
            <SectionHeader
              label="Chats"
              count={agents.length}
              open={chatsOpen}
              onToggle={() => setChatsOpen((s) => !s)}
              collapsed={collapsed}
            />
            {chatsOpen && (
              <ul className={cn('mb-2 space-y-0.5', collapsed ? 'px-0' : 'px-2')}>
                {agents.map((a) => (
                  <li key={a.id}>
                    <SidebarNavItem
                      to={`/chat/${a.id}`}
                      label={a.name}
                      icon={Bot}
                      collapsed={collapsed}
                      avatar={{
                        text: a.name.slice(0, 2).toUpperCase(),
                        color: a.accentColor ?? 'rgb(var(--color-ink))',
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {showSheets && (
          <>
            <SectionHeader
              label="Sheets"
              count={sheets.length}
              open={sheetsOpen}
              onToggle={() => setSheetsOpen((s) => !s)}
              collapsed={collapsed}
            />
            {sheetsOpen && (
              <ul className={cn('space-y-0.5', collapsed ? 'px-0' : 'px-2')}>
                {sheets.map((s) => (
                  <li key={s.id}>
                    <SidebarNavItem
                      to={`/sheets/${s.id}`}
                      label={s.name}
                      icon={(iconProps) => <SheetIcon name={s.icon} {...iconProps} />}
                      collapsed={collapsed}
                      {...(s.accentColor ? { iconStyle: { color: s.accentColor } } : {})}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {showFiles && (
          <>
            <SectionHeader
              label="Files"
              count={folders.length}
              open={filesOpen}
              onToggle={() => setFilesOpen((s) => !s)}
              collapsed={collapsed}
            />
            {filesOpen && (
              <ul className={cn('space-y-0.5', collapsed ? 'px-0' : 'px-2')}>
                {folders.map((f) => (
                  <li key={f.id}>
                    <SidebarNavItem
                      to={`/files/${f.id}`}
                      label={f.name}
                      icon={
                        f.icon
                          ? (iconProps) => <SheetIcon name={f.icon!} {...iconProps} />
                          : FolderIconLucide
                      }
                      collapsed={collapsed}
                      {...(f.accentColor ? { iconStyle: { color: f.accentColor } } : {})}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {visibleFeatures.length > 0 && (
          <ul className={cn('mt-3 space-y-0.5', collapsed ? 'px-0' : 'px-2')}>
            {visibleFeatures.map((f) => (
              <li key={f.to}>
                <SidebarNavItem
                  to={f.to}
                  label={f.label}
                  icon={f.icon}
                  collapsed={collapsed}
                  {...(f.to === '/agents' && !collapsed
                    ? {
                        badge: (
                          <span className="rounded-tight bg-paper-sunk px-1.5 py-0.5 font-mono text-[10px] text-ink-muted">
                            {agents.length}
                          </span>
                        ),
                      }
                    : {})}
                />
              </li>
            ))}
          </ul>
        )}

        {nothingAssigned && <EmptyHint collapsed={collapsed} />}
      </nav>

      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className={cn(
          'inline-flex h-9 items-center gap-2 border-t border-rule px-3 text-xs text-ink-muted ' +
            'transition-colors duration-instant ease-standard hover:bg-paper-sunk hover:text-ink',
          collapsed && 'justify-center px-0',
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
}

function useAccessQuery(
  endpoint: string,
  queryKey: readonly unknown[],
  enabled: boolean,
): boolean {
  const query = useQuery({
    queryKey,
    queryFn: () => apiRequest<{ access: boolean }>(endpoint),
    enabled,
    staleTime: 60_000,
  });
  return query.data?.access === true;
}

function SectionHeader({
  label,
  count,
  open,
  onToggle,
  collapsed,
}: {
  label: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  collapsed: boolean;
}): JSX.Element {
  const Icon =
    label === 'Chats' ? MessageSquare : label === 'Files' ? FolderOpen : Table2;
  if (collapsed) {
    return (
      <div className="mb-1 mt-2 grid place-items-center text-ink-muted" title={label}>
        <Icon className="size-3.5" />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={
        'flex w-full items-center gap-1 px-3 pb-1 pt-3 text-left text-[11px] font-medium text-ink-muted ' +
        'transition-colors duration-instant ease-standard hover:text-ink'
      }
    >
      {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
      <Icon className="size-3" />
      <span>{label}</span>
      <span className="ml-auto rounded-tight bg-paper-sunk px-1.5 py-0.5 font-mono text-[10px]">
        {count}
      </span>
    </button>
  );
}

function EmptyHint({ collapsed }: { collapsed: boolean }): JSX.Element {
  if (collapsed) {
    return (
      <div
        className="mx-2 my-3 grid place-items-center text-ink-muted"
        title="No agents or sheets assigned"
      >
        <Bot className="size-3.5" />
      </div>
    );
  }
  return (
    <div className="px-4 pt-6 text-xs text-ink-muted">
      <p className="font-medium text-ink">Nothing assigned yet</p>
      <p className="mt-1 leading-relaxed">
        An administrator will grant you access to agents, sheets, and files. They&apos;ll appear
        here once they do.
      </p>
    </div>
  );
}

function readBool(key: string, fallback: boolean): boolean {
  if (typeof localStorage === 'undefined') return fallback;
  const v = localStorage.getItem(key);
  if (v === '1') return true;
  if (v === '0') return false;
  return fallback;
}

function writeBool(key: string, value: boolean): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(key, value ? '1' : '0');
}
